import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Gavel, ChevronRight, CircleDot, FileSignature, Scale, PenLine } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { ujmsJudicialFlow, ujmsEnterAdjudication, ujmsBatchSignOrders } from "../services/ujmsApi";
import { Field, inputCls, selectCls, btnGhost, btnPrimary, cls, can, fmtDT } from "./ui";
import { SentenceForm } from "./pages/SentenceDialog";

const METHODS = [["BENCH_TRIAL", "Bench Trial"], ["JURY_TRIAL", "Jury Trial"], ["PLEA", "Plea"], ["MOTION", "On Motion"],
  ["SETTLEMENT", "Settlement"], ["DEFAULT", "Default"], ["AGREED_ORDER", "Agreed Order"], ["ADMINISTRATIVE", "Administrative"]];
const COUNT_FINDINGS = [["GUILTY", "Guilty"], ["NOT_GUILTY", "Not Guilty"], ["DISMISSED", "Dismissed / Nolle"]];

export function JudicialFlowPanel({ caseId, access, onAction, refreshKey, compact = false }) {
  const [flow, setFlow] = useState(null);
  const [adjOpen, setAdjOpen] = useState(false);
  const [signOpen, setSignOpen] = useState(false);
  const load = useCallback(() => ujmsJudicialFlow(caseId).then((r) => { setFlow(r.data); return r.data; }).catch(() => setFlow(null)), [caseId]);
  useEffect(() => { load(); }, [load, refreshKey]);
  if (!flow) return <div className="text-xs text-slate-400 p-3" data-testid="judicial-flow-loading">Loading judicial flow…</div>;
  const current = flow.stages.find((s) => s.status === "current");
  const isJudge = can(access, "order.sign");
  const act = (a) => {
    if (a.kind === "finding") { setAdjOpen(true); return; }
    onAction?.(a);
  };
  return (
    <div className="space-y-3" data-testid="judicial-flow-panel" data-track={flow.track}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold flex items-center gap-1.5">
          <Scale className="w-3.5 h-3.5" /> {flow.track_label} track · {flow.case_number}
        </div>
        <div className="flex items-center gap-3">
          <div className="text-[11px] text-slate-500" data-testid="judicial-flow-current">
            {flow.current === "complete" ? "All stages complete" : `Current stage: ${current?.label}`}
          </div>
          {isJudge && !["DISPOSED", "CLOSED"].includes(flow.case_status) && (
            <button className={cls(btnPrimary, "text-xs py-1")} onClick={() => setAdjOpen(true)} data-testid="flow-enter-finding-btn">
              <Gavel className="w-3.5 h-3.5" /> Enter Finding / Judgment
            </button>
          )}
        </div>
      </div>
      <ol className={cls("flex gap-0 overflow-x-auto pb-1", compact && "text-[10px]")} data-testid="judicial-flow-stepper">
        {flow.stages.map((s, i) => (
          <li key={s.key} className="flex items-center shrink-0" data-testid={`flow-stage-${s.key}`} data-status={s.status}>
            <div className={cls("flex items-center gap-1.5 px-2.5 py-1.5 border rounded-sm",
              s.status === "done" && "bg-emerald-50 border-emerald-300 text-emerald-800",
              s.status === "current" && "bg-ujms text-white border-ujms-dark shadow",
              s.status === "pending" && "bg-white border-slate-200 text-slate-500")}>
              {s.status === "done" ? <Check className="w-3.5 h-3.5" /> : s.status === "current" ? <CircleDot className="w-3.5 h-3.5" /> : <span className="w-3.5 h-3.5 inline-grid place-items-center text-[10px] font-bold">{i + 1}</span>}
              <span className="text-xs font-semibold whitespace-nowrap">{s.label}</span>
            </div>
            {i < flow.stages.length - 1 && <ChevronRight className="w-4 h-4 text-slate-300 mx-0.5 shrink-0" />}
          </li>
        ))}
      </ol>
      {current && (
        <div className="border border-ujms/40 bg-ujms-soft rounded-sm p-3" data-testid="judicial-flow-current-card">
          <div className="text-sm font-bold text-ujms-deep">{current.label}</div>
          <div className="text-xs text-slate-600 mt-0.5">{current.detail}</div>
          {isJudge && current.actions?.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2.5">
              {current.actions.map((a) => (
                <button key={a.key} onClick={() => act(a)} data-testid={`flow-action-${a.key}`}
                  className={cls(a.kind === "finding" ? btnPrimary : btnGhost, "text-xs")}>
                  {a.kind === "finding" && <Gavel className="w-3.5 h-3.5" />} {a.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      {flow.adjudication && (
        <div className="text-xs text-slate-700 border border-slate-200 rounded-sm px-3 py-2 flex items-start gap-2" data-testid="judicial-flow-adjudication">
          <FileSignature className="w-4 h-4 text-ujms-dark shrink-0 mt-0.5" />
          <div>
            <b>{flow.adjudication.label}</b> — {flow.adjudication.method?.replace(/_/g, " ").toLowerCase()} · entered {fmtDT(flow.adjudication.entered_at)} by {flow.adjudication.entered_by}
            {flow.adjudication.fine_amount ? ` · fine $${Number(flow.adjudication.fine_amount).toLocaleString()}` : ""}
            {flow.adjudication.amount ? ` · $${Number(flow.adjudication.amount).toLocaleString()}` : ""}
            {flow.pending_orders > 0 && <span className="ml-2 text-amber-700 font-semibold">{flow.pending_orders} order(s) awaiting signature</span>}
          </div>
        </div>
      )}
      {flow.pending_order_list?.length > 0 && (
        <div className="border border-amber-300 bg-amber-50 rounded-sm px-3 py-2.5" data-testid="judicial-flow-pending-orders">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="text-xs text-amber-900">
              <b>Orders awaiting the Court's signature:</b>
              <ul className="mt-1 space-y-0.5">
                {flow.pending_order_list.map((o) => <li key={o.id} className="flex items-center gap-1.5" data-testid={`flow-pending-order-${o.id}`}><FileSignature className="w-3 h-3" /> {o.title} <span className="text-[10px] uppercase text-amber-700">{o.order_type}</span></li>)}
              </ul>
            </div>
            {isJudge && (
              <button className={cls(btnPrimary, "text-xs")} onClick={() => setSignOpen(true)} data-testid="flow-sign-orders-btn">
                <PenLine className="w-3.5 h-3.5" /> {flow.pending_order_list.length === 2 ? "Sign Both Orders" : `Sign All Orders (${flow.pending_order_list.length})`}
              </button>
            )}
          </div>
        </div>
      )}
      {adjOpen && <AdjudicationDialog flow={flow} caseId={caseId} onClose={() => setAdjOpen(false)}
        onDone={async (r) => { setAdjOpen(false); const f = await load(); onAction?.({ kind: "refresh" }); if (r?.order && f?.pending_order_list?.length) setSignOpen(true); }} />}
      {signOpen && flow.pending_order_list?.length > 0 && (
        <SignOrdersDialog orders={flow.pending_order_list} onClose={() => setSignOpen(false)}
          onDone={() => { setSignOpen(false); load(); onAction?.({ kind: "refresh" }); }} />
      )}
    </div>
  );
}

function SignOrdersDialog({ orders, onClose, onDone }) {
  const [confirm, setConfirm] = useState(false);
  const [enter, setEnter] = useState(true);
  const [initials, setInitials] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const r = await ujmsBatchSignOrders({ order_ids: orders.map((o) => o.id), confirm: true, initials, enter });
      toast.success(`${r.data.signed} order(s) signed${enter ? ` and ${r.data.entered} entered on the docket` : ""}${r.data.skipped?.length ? ` · ${r.data.skipped.length} skipped` : ""}`);
      onDone?.(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Signing failed"); }
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="flow-sign-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><PenLine className="w-4 h-4 text-ujms-dark" /> Sign {orders.length === 2 ? "both orders" : `${orders.length} orders`}</DialogTitle></DialogHeader>
        <ul className="text-sm space-y-1" data-testid="flow-sign-list">
          {orders.map((o) => <li key={o.id} className="flex items-center gap-2"><FileSignature className="w-3.5 h-3.5 text-ujms-dark" /> {o.title}</li>)}
        </ul>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Initials (optional)"><input className={inputCls} value={initials} onChange={(e) => setInitials(e.target.value.toUpperCase().slice(0, 4))} data-testid="flow-sign-initials" /></Field>
          <label className="flex items-center gap-2 text-sm mt-5"><input type="checkbox" checked={enter} onChange={(e) => setEnter(e.target.checked)} data-testid="flow-sign-enter" /> Enter on the docket after signing</label>
        </div>
        <label className="flex items-start gap-2 text-sm border border-slate-200 rounded-sm p-2.5 bg-slate-50">
          <input type="checkbox" checked={confirm} onChange={(e) => setConfirm(e.target.checked)} className="mt-0.5" data-testid="flow-sign-confirm" />
          <span>I am the judicial officer of record and I apply my electronic signature to each order listed above. <span className="text-xs text-slate-500">Prototype electronic signature — no governmental certification claimed.</span></span>
        </label>
        <DialogFooter>
          <button className={btnGhost} onClick={onClose} data-testid="flow-sign-cancel">Cancel</button>
          <button className={btnPrimary} disabled={!confirm || busy} onClick={submit} data-testid="flow-sign-submit"><PenLine className="w-3.5 h-3.5" /> {busy ? "Signing…" : "Sign & Enter"}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function AdjudicationDialog({ flow, caseId, onClose, onDone }) {
  const openCharges = flow.charges.filter((c) => !["DISMISSED", "CONVICTED", "ACQUITTED", "MERGED", "OTHER_DISPOSITION"].includes(c.status));
  const [finding, setFinding] = useState(flow.findings[0]?.code || "");
  const [method, setMethod] = useState("BENCH_TRIAL");
  const [amount, setAmount] = useState("");
  const [fine, setFine] = useState("");
  const [months, setMonths] = useState("12");
  const [notes, setNotes] = useState("");
  const [counts, setCounts] = useState(() => Object.fromEntries(openCharges.map((c) => [c.id, "GUILTY"])));
  const [busy, setBusy] = useState(false);
  const [sentenceStep, setSentenceStep] = useState(null);
  const money = ["civil", "family", "probate"].includes(flow.track);
  const submit = async () => {
    setBusy(true);
    try {
      const body = { finding, method, notes,
        amount: money && amount !== "" ? Number(amount) : null,
        fine_amount: flow.track === "traffic" && fine !== "" ? Number(fine) : null,
        supervision_months: finding === "SUPERVISION" ? Number(months) : null,
        charge_findings: flow.track === "criminal" ? openCharges.map((c) => ({ charge_id: c.id, finding: counts[c.id] })) : [] };
      const r = await ujmsEnterAdjudication(caseId, body);
      toast.success(`${r.data.adjudication.label} entered — "${r.data.order.title}" drafted for signature${r.data.case_status === "DISPOSED" ? " · case disposed" : ""}`);
      const guilty = (flow.track === "criminal" && Object.values(counts).some((v) => v === "GUILTY")) || (flow.track === "juvenile" && finding === "DELINQUENT");
      if (guilty && r.data.case_status !== "DISPOSED") {
        setSentenceStep({ label: r.data.adjudication.label, orderTitle: r.data.order.title,
          charges: flow.charges.map((c) => (counts[c.id] === "GUILTY" ? { ...c, status: "CONVICTED" } : c)) });
        setBusy(false);
        return;
      }
      onDone?.(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to enter finding"); }
    setBusy(false);
  };
  if (sentenceStep) {
    return (
      <Dialog open onOpenChange={(v) => !v && onDone?.()}>
        <DialogContent className="max-w-2xl" data-testid="adjudication-sentence-step">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Scale className="w-4 h-4 text-ujms-dark" /> Step 2 of 2 — Sentence following {flow.track === "juvenile" ? "adjudication" : "finding of guilty"}</DialogTitle></DialogHeader>
          <div className="text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-sm px-2.5 py-1.5 flex items-center gap-2" data-testid="adjudication-sentence-banner">
            <Check className="w-3.5 h-3.5" /> <b>{sentenceStep.label}</b> entered · "{sentenceStep.orderTitle}" drafted. Now impose the sentence — the Sentencing Order is drafted for signature alongside it.
          </div>
          <SentenceForm caseId={caseId} charges={sentenceStep.charges} draftOnly onDone={(r) => onDone?.(r)} onCancel={() => onDone?.()} cancelLabel="Sentence later" testPrefix="flow-sentence" />
        </DialogContent>
      </Dialog>
    );
  }
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl" data-testid="adjudication-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Gavel className="w-4 h-4 text-ujms-dark" /> Enter finding / judgment — {flow.track_label}</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500">Records the Court's adjudication on the docket, applies it to each count where applicable, and auto-drafts the matching order (Cook County format) for your signature.</p>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Finding / judgment">
            <select className={selectCls} value={finding} onChange={(e) => setFinding(e.target.value)} data-testid="adj-finding">
              {flow.findings.map((f) => <option key={f.code} value={f.code}>{f.label}</option>)}
            </select>
          </Field>
          <Field label="Method">
            <select className={selectCls} value={method} onChange={(e) => setMethod(e.target.value)} data-testid="adj-method">
              {METHODS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </Field>
          {money && <Field label="Judgment amount ($, optional)"><input type="number" min="0" step="0.01" className={inputCls} value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="adj-amount" /></Field>}
          {flow.track === "traffic" && ["GUILTY", "SUPERVISION"].includes(finding) && (
            <Field label="Fine & costs ($)"><input type="number" min="0" step="1" className={inputCls} value={fine} onChange={(e) => setFine(e.target.value)} placeholder="e.g. 250" data-testid="adj-fine" /></Field>
          )}
          {finding === "SUPERVISION" && <Field label="Supervision (months)"><input type="number" min="1" max="24" className={inputCls} value={months} onChange={(e) => setMonths(e.target.value)} data-testid="adj-months" /></Field>}
        </div>
        {flow.track === "criminal" && (
          <div data-testid="adj-counts">
            <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold mb-1">Finding on each open count</div>
            {openCharges.length === 0 && <div className="text-xs text-amber-700">No open counts on this case — add charges before entering a finding.</div>}
            {openCharges.map((c) => (
              <div key={c.id} className="flex items-center justify-between gap-3 border-b border-slate-100 py-1.5 text-sm">
                <div><span className="font-semibold">Count {c.count_number}</span> <span className="font-mono text-xs">{c.statute_code}</span> — {c.description}</div>
                <select className={`${selectCls} w-44`} value={counts[c.id]} onChange={(e) => setCounts({ ...counts, [c.id]: e.target.value })} data-testid={`adj-count-${c.count_number}`}>
                  {COUNT_FINDINGS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
            ))}
          </div>
        )}
        <Field label="Findings of record / notes (optional)">
          <textarea className={`${inputCls} h-16`} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="adj-notes" />
        </Field>
        <DialogFooter>
          <button className={btnGhost} onClick={onClose} data-testid="adj-cancel">Cancel</button>
          <button className={btnPrimary} disabled={busy || (flow.track === "criminal" && openCharges.length === 0)} onClick={submit} data-testid="adj-submit">
            <Gavel className="w-3.5 h-3.5" /> {busy ? "Entering…" : "Enter Finding & Draft Order"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
